import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// This site's own copy, NOT the shared src/assets/i18n files.
//
// The shared locale files are bundled into busLineApp and busLineSrv.
// Marketing copy and a privacy policy are ~20 kB per language that
// neither of those two will ever render, and putting them there would add ~160 kB
// to the phone app's bundle to ship strings it cannot reach. So the web copy
// lives here, in eight files with the same eight language codes.
//
// The one thing this site does need from the shared files is the `errorcode.*`
// section: @library/providers/ApiClientProvider looks up `errorcode.NNNNNN` to
// turn a numeric API error into a sentence, and the verify-email / new-password
// screens are the only places here that can produce one. That is loaded on
// demand — see ensureErrorCodes() — so the landing page never pays for it.
import en from './locales/en.json';

/** Language code → its own name, for the picker. Matches the phone apps' codes. */
export const LANGUAGES: ReadonlyArray<{ code: string; label: string }> = [
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'hr', label: 'Hrvatski' },
    { code: 'hu', label: 'Magyar' },
    { code: 'it', label: 'Italiano' },
    { code: 'po', label: 'Polski' },
];

/**
 * `po` is the repo's internal code for Polish (LANG_CODE_MAP id 8), not a valid
 * BCP-47 tag. Anything the browser or a crawler sees — <html lang>, hreflang —
 * has to be translated back to `pl`.
 */
export const bcp47 = (code: string): string => (code === 'po' ? 'pl' : code);

const STORAGE_KEY = 'bgweb_lang';

// Vite turns each of these into its own chunk, fetched only when asked for.
const LAZY_COPY: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
    de: () => import('./locales/de.json'),
    es: () => import('./locales/es.json'),
    fr: () => import('./locales/fr.json'),
    hr: () => import('./locales/hr.json'),
    hu: () => import('./locales/hu.json'),
    it: () => import('./locales/it.json'),
    po: () => import('./locales/po.json'),
};

// The shared app locales, for their `errorcode` section only. Each of these files
// is ~135 kB and only ~38 kB of that is error codes, so the chunk carries about
// 100 kB it does not use. Accepted deliberately: slicing it would mean either a
// generated file that can drift from the source or a build plugin, and this chunk
// is only ever fetched by someone who arrived from a verification email.
const LAZY_ERRORCODES: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
    en: () => import('@assets/i18n/EN.json'),
    de: () => import('@assets/i18n/DE.json'),
    es: () => import('@assets/i18n/ES.json'),
    fr: () => import('@assets/i18n/FR.json'),
    hr: () => import('@assets/i18n/HR.json'),
    hu: () => import('@assets/i18n/HU.json'),
    it: () => import('@assets/i18n/IT.json'),
    po: () => import('@assets/i18n/PO.json'),
};

/**
 * The phone app's help articles, for /help. Unlike LAZY_ERRORCODES these files
 * are small (~12 kB) and are the WHOLE payload we want, so they are merged as
 * their own `help` namespace rather than sliced out of a bigger file.
 *
 * Shared with the app on purpose: /help must say exactly what Settings > How to
 * use the app says, and two copies of fourteen articles in eight languages is
 * two copies that diverge. The trade is that translating them once serves both.
 */
const LAZY_HELP: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
    en: () => import('@assets/i18n/help/EN.json'),
    de: () => import('@assets/i18n/help/DE.json'),
    es: () => import('@assets/i18n/help/ES.json'),
    fr: () => import('@assets/i18n/help/FR.json'),
    hr: () => import('@assets/i18n/help/HR.json'),
    hu: () => import('@assets/i18n/help/HU.json'),
    it: () => import('@assets/i18n/help/IT.json'),
    po: () => import('@assets/i18n/help/PO.json'),
};

const isKnown = (code: string | null | undefined): code is string =>
    !!code && LANGUAGES.some((l) => l.code === code);

/**
 * Which language to open in. Explicit beats remembered beats the browser:
 *   1. ?lang=hr in the URL — what an hreflang link or a shared URL carries
 *   2. a previous choice from this browser
 *   3. navigator.language ('pl-PL' → 'po')
 *   4. English
 */
export const detectLanguage = (): string => {
    if (typeof window === 'undefined') return 'en';

    const fromQuery = new URLSearchParams(window.location.search).get('lang');
    if (isKnown(fromQuery)) return fromQuery;

    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (isKnown(stored)) return stored;
    } catch { /* private mode, ignore */ }

    const nav = (window.navigator.language || 'en').slice(0, 2).toLowerCase();
    const mapped = nav === 'pl' ? 'po' : nav;
    return isKnown(mapped) ? mapped : 'en';
};

void i18n
    .use(initReactI18next)
    .init({
        resources: {
            en: { translation: en },
        },
        lng: 'en',
        fallbackLng: 'en',
        // Every other language falls back to the bundled English, so a missing or
        // still-loading locale degrades to readable text rather than raw keys.
        interpolation: {
            escapeValue: false,
        },
    });

/** Fetch a locale chunk and switch to it. No-op for 'en' and for anything loaded. */
export const loadLanguage = async (code: string): Promise<void> => {
    if (!isKnown(code)) return;

    if (code !== 'en' && !i18n.hasResourceBundle(code, 'translation')) {
        const loader = LAZY_COPY[code];
        if (loader) {
            const module = await loader();
            i18n.addResourceBundle(code, 'translation', module.default, true, true);
        }
    }

    await i18n.changeLanguage(code);

    try {
        window.localStorage.setItem(STORAGE_KEY, code);
    } catch { /* ignore */ }

    document.documentElement.lang = bcp47(code);
};

/**
 * Merge the shared `errorcode.*` bundle for the active language. Called by the two
 * screens that can surface an API error; everything else on the site is static and
 * never needs it.
 *
 * Only the `errorcode` section is merged, not the whole file — the rest is phone-app
 * strings that would sit in memory competing with this site's own keys for names.
 */
const errorCodesLoaded = new Set<string>();

export const ensureErrorCodes = async (): Promise<void> => {
    // Tracked per language rather than by probing for a key: switching language
    // after the first load has to fetch that language's codes too, and
    // i18n.exists() would answer for the fallback bundle and skip the fetch.
    const code = LAZY_ERRORCODES[i18n.language] ? i18n.language : 'en';
    if (errorCodesLoaded.has(code)) return;
    errorCodesLoaded.add(code);

    try {
        const module = await LAZY_ERRORCODES[code]();
        const errorcode = (module.default as { errorcode?: Record<string, unknown> }).errorcode;
        if (errorcode) {
            i18n.addResourceBundle(code, 'translation', { errorcode }, true, true);
        }
    } catch {
        // A failed chunk fetch must not take the page down with it: without the
        // bundle an API error shows the generic fallback sentence instead of the
        // code-specific one, which is a degradation, not a breakage.
        errorCodesLoaded.delete(code);
    }
};

/**
 * Merge the app's help articles for the active language, as the `help`
 * namespace. Called only by the /help pages — the landing page never pays for
 * fourteen articles it does not render.
 *
 * Same per-language tracking as ensureErrorCodes, and for the same reason:
 * i18n.exists() would answer out of the English fallback bundle and skip the
 * fetch for every other language.
 */
const helpLoaded = new Set<string>();

export const ensureHelpCopy = async (): Promise<boolean> => {
    const code = LAZY_HELP[i18n.language] ? i18n.language : 'en';
    if (helpLoaded.has(code)) return true;
    helpLoaded.add(code);

    try {
        const module = await LAZY_HELP[code]();
        i18n.addResourceBundle(code, 'help', module.default, true, true);
        return true;
    } catch {
        // Unlike the errorcode bundle there is no graceful degradation here: with
        // no copy there is no page. The caller renders an error instead of a wall
        // of raw keys, and a retry is allowed to try the fetch again.
        helpLoaded.delete(code);
        return false;
    }
};

export default i18n;
